import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './BotSelector.module.scss';

import Loader from 'component/atom/loader/Loader';
import { useFetch } from 'hooks/useFetch';
import { scriptEndpoints } from 'hooks/endpoints';
import { useUserContext } from 'app-context/user/UserContext';

const downArrow = '/src/svg/arrows/chevron-down.svg';

const getInitials = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0]?.toUpperCase())
    .join('')
    .slice(0, 3);

const BotSelector = ({
  sidebarOpen = true,
  bots = [],
  isLoading,
  isFetching,
}) => {
  const { selectedBot, setSelectedBot } = useUserContext();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handlePick = (bot) => {
    setSelectedBot(bot);
    localStorage.setItem('selectedBot', JSON.stringify(bot));
    setIsOpen(false);
  };

  useEffect(() => {
    const onClickOutside = (e) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem('selectedBot');
    if (!stored) return;
    try {
      setSelectedBot(JSON.parse(stored));
    } catch {
      localStorage.removeItem('selectedBot');
    }
  }, [setSelectedBot]);

  useEffect(() => {
    if (!bots.length) return;
    if (selectedBot?.botId) return;

    const first = bots[0];
    setSelectedBot(first);
    localStorage.setItem('selectedBot', JSON.stringify(first));
  }, [bots, selectedBot?.botId, setSelectedBot]);

  useEffect(() => {
    if (!bots.length) return;
    if (!selectedBot?.botId) return;

    const exists = bots.some((b) => b.botId === selectedBot.botId);
    if (!exists) {
      setSelectedBot(null);
      localStorage.removeItem('selectedBot');
    }
  }, [bots, selectedBot?.botId, setSelectedBot]);

  return (
    <div className={styles.botSelector} ref={dropdownRef}>
      <button
        type='button'
        className={`${styles.botTrigger} ${!sidebarOpen ? styles['center-avatar'] : ''}`}
        onClick={() => setIsOpen((v) => !v)}
        aria-haspopup='listbox'
        aria-expanded={isOpen}
      >
        <div className={styles.botTriggerInner}>
          {selectedBot?.src ? (
            <img className={styles.botAvatar} src={selectedBot.src} alt='' />
          ) : (
            <div className={styles.botAvatarFallback}>
              {getInitials(selectedBot?.botName || 'Bot')}
            </div>
          )}

          {sidebarOpen && (
            <div className={styles.botText}>
              <p className={styles.botName}>
                {selectedBot?.botName || 'Select Bot'}
              </p>
            </div>
          )}
        </div>

        {sidebarOpen && (
          <img className={styles.botChevron} src={downArrow} alt='' />
        )}
      </button>

      {isOpen && sidebarOpen && (
        <div className={styles.botMenu} role='listbox'>
          {isLoading || isFetching ? (
            <div className={styles.botMenuLoading}>
              <Loader />
            </div>
          ) : (
            bots.map((bot) => {
              const active = selectedBot?.botId === bot.botId;

              return (
                <button
                  key={bot.botId}
                  type='button'
                  role='option'
                  aria-selected={active}
                  className={`${styles.botOption} ${
                    active ? styles.activeBotOption : ''
                  }`}
                  onClick={() => handlePick(bot)}
                >
                  {bot?.src ? (
                    <img className={styles.botAvatar} src={bot.src} alt='' />
                  ) : (
                    <div className={styles.botAvatarFallback}>
                      {getInitials(bot?.botName)}
                    </div>
                  )}

                  <div className={styles.botText}>
                    <p className={styles.botNameMaped}>{bot.botName}</p>
                    <p className={styles.botSubtitle}>
                      {bot?.description || ''}
                    </p>
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default BotSelector;
